import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import { ReactComponent as EditIcon } from "../assets/img/tool_icons/Edit.svg";
import { ReactComponent as ViewIcon } from "../assets/img/tool_icons/View.svg";
import { useCustomAuth } from "../contexts/AuthContext";
import OptionsMenu from "./OptionsMenu";
import AnnotatorField from "./AnnotatorField";
import { requireAnnotatorAssignment } from "../utils/annotatorAssignment";
import { resolveItemFolderPath } from "../utils/annotationSheetNavigation";

const FileTable = ({ items, sortConfig, handleSort, handleFolderClick, handleSheetClick, handleSheetDelete, handleSheetRename, showPath = false, itemPaths = {},
    itemFolderPaths = {}, currentPath = [], displayNames = {}, allUsers = [], onAnnotatorsChange = () => {}, handleFolderDelete, handleFolderRename }) => {
    // Get admin privileges
    const {userData, isAdminMode} = useCustomAuth();
    const isAdmin = userData?.roles.includes("ADMIN") || userData?.roles.includes("ROOT") || false

    // COMMENTED OUT: Helper function to recursively get file-level annotators for a folder
    // const getFileLevelAnnotators = (folderNode) => {
    //     if (!folderNode || !folderNode.children) return [];
    //     
    //     const fileLevelAnnotators = [];
    //     const seenEmails = new Set();
    //     
    //     // Recursive function to traverse all nested folders and files
    //     const traverseNode = (node) => {
    //         if (!node || !node.children) return;
    //         
    //         Object.values(node.children).forEach(childNode => {
    //             if (childNode.sheet_id && childNode.sheet_data?.assigned_annotators) {
    //                 // This is a file - collect its annotators
    //                 childNode.sheet_data.assigned_annotators.forEach(annotator => {
    //                     if (!seenEmails.has(annotator.email)) {
    //                         seenEmails.add(annotator.email);
    //                         fileLevelAnnotators.push(annotator);
    //                     }
    //                 });
    //             } else if (childNode.children) {
    //                 // This is a subfolder - recursively traverse it
    //                 traverseNode(childNode);
    //             }
    //         });
    //     };
    //     
    //     // Start the recursive traversal
    //     traverseNode(folderNode);
    //     
    //     return fileLevelAnnotators;
    // };
    // useEffect(() => {
    //     console.log('OSDEBUG AN FINAL allUsers: ', allUsers);
    //     console.log('OSDBEUG AN FINAL items: ', items);
    // })

    // If there are no items, show a message
    if (!items || items.length === 0) {
        return (
            <Box sx={{
                py: 4,
                display: 'flex',
                justifyContent: 'center',
                border: '1px dashed #e0e0e0',
                borderRadius: '4px',
                bgcolor: '#fafafa'
            }}>
                <Typography sx={{ color: '#757575' }}>
                    No files available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <TableContainer component={Paper} variant="outlined" sx={{
                    boxShadow: 'none',
                    borderRadius: '4px',
                    border: 'none',
                    maxHeight: '100%',
                    overflow: 'auto'
            }}>
                <Table>
                    <TableHead sx={{ bgcolor: 'transparent' }}>
                        <TableRow>
                            <TableCell
                                sx={{
                                    fontWeight: 'normal',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    borderBottom: '1px solid #eeeeee',
                                    paddingY: 1.5,
                                    fontSize: '0.875rem',
                                    color: '#333',
                                    userSelect: 'none'
                                }}
                                onClick={() => handleSort('filename')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                                    FILE NAME
                                    <Box sx={{ width: 20, ml: 0.5, display: 'flex', justifyContent: 'center' }}>
                                        {sortConfig.key === 'filename' ? (
                                            sortConfig.direction === 'asc'
                                                ? <ArrowUpwardIcon fontSize="small" />
                                                : <ArrowDownwardIcon fontSize="small" />
                                        ) : null}
                                    </Box>
                                </Box>
                            </TableCell>
                            {showPath && (
                                <TableCell
                                    align="left"
                                    sx={{
                                        fontWeight: 'normal',
                                        borderBottom: '1px solid #eeeeee',
                                        paddingY: 1.5,
                                        fontSize: '0.875rem',
                                        color: '#333',
                                        userSelect: 'none'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                                        FULL PATH
                                    </Box>
                                </TableCell>
                            )}
                            <TableCell
                                align="left"
                                sx={{
                                    fontWeight: 'normal',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    borderBottom: '1px solid #eeeeee',
                                    paddingY: 1.5,
                                    fontSize: '0.875rem',
                                    color: '#333',
                                    userSelect: 'none'
                                }}
                                onClick={() => handleSort('owner')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                                    OWNER
                                    <Box sx={{ width: 20, ml: 0.5, display: 'flex', justifyContent: 'center' }}>
                                        {sortConfig.key === 'owner' ? (
                                            sortConfig.direction === 'asc'
                                                ? <ArrowUpwardIcon fontSize="small" />
                                                : <ArrowDownwardIcon fontSize="small" />
                                        ) : null}
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell
                                align="left"
                                sx={{
                                    fontWeight: 'normal',
                                    borderBottom: '1px solid #eeeeee',
                                    paddingY: 1.5,
                                    fontSize: '0.875rem',
                                    color: '#333'
                                }}
                                >
                                ANNOTATORS
                                </TableCell>
                            <TableCell
                                align="left"
                                sx={{
                                    fontWeight: 'normal',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    borderBottom: '1px solid #eeeeee',
                                    paddingY: 1.5,
                                    fontSize: '0.875rem',
                                    color: '#333',
                                    userSelect: 'none'
                                }}
                                onClick={() => handleSort('updated')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                                    LAST UPDATED
                                    <Box sx={{ width: 20, ml: 0.5, display: 'flex', justifyContent: 'center' }}>
                                        {sortConfig.key === 'updated' ? (
                                            sortConfig.direction === 'asc'
                                                ? <ArrowUpwardIcon fontSize="small" />
                                                : <ArrowDownwardIcon fontSize="small" />
                                        ) : null}
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell
                                align="left"
                                sx={{
                                    fontWeight: 'normal',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    borderBottom: '1px solid #eeeeee',
                                    paddingY: 1.5,
                                    fontSize: '0.875rem',
                                    color: '#333',
                                    userSelect: 'none'
                                }}
                                onClick={() => handleSort('status')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                                    STATUS
                                    <Box sx={{ width: 20, ml: 0.5, display: 'flex', justifyContent: 'center' }}>
                                        {sortConfig.key === 'status' ? (
                                            sortConfig.direction === 'asc'
                                                ? <ArrowUpwardIcon fontSize="small" />
                                                : <ArrowDownwardIcon fontSize="small" />
                                        ) : null}
                                    </Box>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => {
                            const [key, node] = item;
                            const containingFolderPath = resolveItemFolderPath({
                                item,
                                mappedPath: itemFolderPaths[key],
                                currentPath,
                            });
                            
                            // Get the real filename - use the node's key as default value
                            const fileName = displayNames[key] || node.sheet_data?.displayed_filename || key;
                            const annotatorAssignment = requireAnnotatorAssignment(
                                node.sheet_id
                                    ? node.sheet_data?.assigned_annotators
                                    : node.folder_annotators,
                                `assigned_annotators for ${node.sheet_id || key}`
                            );

                            // Get real status from sheet_data if available
                            let statusColor, statusText;
                            const status = node.sheet_data?.status || '-';

                            if (status === '-') {
                                statusColor = '#cccccc'; // Light gray for no status
                                statusText = '-';
                            } else if (status.toLowerCase().includes('not') || status.toLowerCase().includes('start')) {
                                statusColor = '#e74c3c';
                                statusText = 'Not Started';
                            } else if (status.toLowerCase().includes('complete') || status.toLowerCase().includes('done')) {
                                statusColor = '#27ae60';
                                statusText = 'Completed';
                            } else {
                                statusColor = '#f1c40f';
                                statusText = 'In Progress';
                            }

                            // Get real last updated time from sheet_data if available
                            let lastUpdated = '-';
                            if (node.sheet_data?.last_updated) {
                                // console.log("node sheet data", node.sheet_data);
                                const updateDate = new Date(node.sheet_data.last_updated);
                                const today = new Date();
                                const yesterday = new Date();
                                yesterday.setDate(yesterday.getDate() - 1);

                                if (updateDate.toDateString() === today.toDateString()) {
                                    const localTimeString = updateDate.toLocaleTimeString([], {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      });
                                      lastUpdated = `Today at ${localTimeString}`;
                                } else if (updateDate.toDateString() === yesterday.toDateString()) {
                                    lastUpdated = 'Yesterday';
                                } else {
                                    // Format as 'Month Day' (e.g., 'April 3')
                                    lastUpdated = updateDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
                                }
                            }

                            // Get real owner information from sheet_data if available
                            // const owner = node.sheet_data?.owner || '-';
                            const owner =  '-';

                            return (
                                <TableRow
                                    key={node.sheet_id ? `${node.sheet_id}:${key}` : key}
                                    hover
                                    sx={{
                                        cursor: (isAdmin && isAdminMode) ? 'default' : 'pointer',
                                        '&:hover': { bgcolor: '#f9f9f9' },
                                        '& .MuiTableCell-root': {
                                            borderBottom: index === items.length - 1 ? 'none' : '1px solid #eeeeee',
                                            paddingY: 1.5,
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                    onClick={() => node.sheet_id ? ((isAdmin && isAdminMode) ? () => {} : handleSheetClick(node.sheet_id, "edit", containingFolderPath)) : handleFolderClick(key)}
                                >
                                    <TableCell sx={{ position: 'relative' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {node.sheet_id ?
                                                <DescriptionIcon sx={{ mr: 2 }} /> :
                                                <FolderIcon sx={{ mr: 2 }} />
                                            }
                                            {fileName}
                                        </Box>
                                        {isAdmin && isAdminMode && node.sheet_id && (
                                            <Box sx={{
                                                position: 'absolute',
                                                right: 16,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                <Box
                                                    component={ViewIcon}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        node.sheet_id ? handleSheetClick(node.sheet_id, "view", containingFolderPath) : handleFolderClick(key);
                                                    }}
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        p: 0.25,
                                                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.1)" }
                                                    }}
                                                />
                                                <Box
                                                    component={EditIcon}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        node.sheet_id ? handleSheetClick(node.sheet_id, "edit", containingFolderPath) : handleFolderClick(key);
                                                    }}
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        p: 0.25,
                                                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.1)" }
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </TableCell>
                                    {showPath && (
                                        <TableCell align="left">
                                            <Typography sx={{
                                                fontWeight: 'normal',
                                                fontSize: '0.875rem',
                                                color: '#666'
                                            }}>
                                                {itemPaths[key] || node.sheet_data?.path || '-'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell align="left">
                                        {owner}
                                    </TableCell>
                                    <TableCell align="left">
                                        <AnnotatorField
                                          parentLevelAnnotators={annotatorAssignment.parent}
                                          currentLevelAnnotators={annotatorAssignment.current}
                                          fileLevelAnnotators={[]}
                                          allUsers={allUsers}
                                          isAdmin={isAdmin && isAdminMode}
                                          onChange={(u, isAdd) => onAnnotatorsChange(node, u, isAdd)}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        {lastUpdated}
                                    </TableCell>
                                    <TableCell align="left" sx={{ position: 'relative' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {status === '-' ? (
                                                <Typography sx={{
                                                    fontWeight: 'normal',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    {statusText}
                                                </Typography>
                                            ) : (
                                                <>
                                                    <Box sx={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        bgcolor: statusColor
                                                    }} />
                                                    <Typography sx={{
                                                        fontWeight: 'normal',
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {statusText}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        {(isAdmin && isAdminMode) && (
                                            node.sheet_id ? 
                                            <Box sx={{
                                                position: 'absolute',
                                                right: 16,
                                                top: '50%',
                                                transform: 'translateY(-50%)'
                                            }}>
                                                <OptionsMenu 
                                                    onDelete={() => handleSheetDelete(node.sheet_id)} 
                                                    onRename={(newName) => handleSheetRename(node.sheet_id, newName)}
                                                    annotators={annotatorAssignment.current}
                                                    parentLevelAnnotators={annotatorAssignment.parent}
                                                    fileLevelAnnotators={[]}
                                                    allUsers={allUsers}
                                                    isAdmin={isAdmin}
                                                    isAdminMode={isAdminMode}
                                                    onAnnotatorsChange={(user, isAdd) => onAnnotatorsChange(node, user, isAdd)}
                                                />
                                            </Box> :
                                            <Box sx={{
                                                position: 'absolute',
                                                right: 16,
                                                top: '50%',
                                                transform: 'translateY(-50%)'
                                            }}>
                                                <OptionsMenu
                                                    onDelete={() => handleFolderDelete(node)}
                                                    onRename={(newName) => handleFolderRename(node, newName)}
                                                    annotators={annotatorAssignment.current}
                                                    parentLevelAnnotators={annotatorAssignment.parent}
                                                    fileLevelAnnotators={[]}
                                                    allUsers={allUsers}
                                                    isAdmin={isAdmin}
                                                    isAdminMode={isAdminMode}
                                                    onAnnotatorsChange={(user, isAdd) => onAnnotatorsChange(node, user, isAdd)}
                                                />
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default FileTable;
